// src/components/MessageToneSelector.jsx
//
// Coach-facing picker for the AI Message Composer's tone. Lives in
// ProfileSettings under "Coach voice" so coaches set it once and the
// AI generates every future message in that style. Without this every
// generated message defaulted to "warm but professional" hardcoded
// in the system prompt — the marketing claim "drafts in your voice"
// was technically untrue.
//
// Auto-saves on change (writes `messageTone` to users/{uid}). No
// separate Save button — the component owns its own persistence so
// it doesn't have to plug into ProfileSettings' multi-field save
// flow.

import React, { useState, useEffect } from 'react';
import { doc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import toast from 'react-hot-toast';
import {
  MESSAGE_TONES,
  DEFAULT_MESSAGE_TONE_ID,
} from '@/constants/messageTones';
import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function MessageToneSelector({ uid }) {
  const [selectedId, setSelectedId] = useState(DEFAULT_MESSAGE_TONE_ID);
  const [savingId, setSavingId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Subscribe to the user doc so the selection stays in sync if it's
  // changed from another device / tab. onSnapshot is cheap here since
  // ProfileSettings already subscribes — Firestore dedupes the listener.
  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      if (!snap.exists()) {
        setLoaded(true);
        return;
      }
      const data = snap.data() || {};
      setSelectedId(data.messageTone || DEFAULT_MESSAGE_TONE_ID);
      setLoaded(true);
    }, () => setLoaded(true));
    return unsub;
  }, [uid]);

  const pick = async (toneId) => {
    if (!uid || toneId === selectedId) return;
    setSavingId(toneId);
    try {
      await updateDoc(doc(db, 'users', uid), {
        messageTone: toneId,
        updatedAt: serverTimestamp(),
      });
      toast.success('Voice updated');
    } catch (err) {
      console.error('[MessageToneSelector] save failed', err);
      toast.error("Hmm, that didn't save. One more try?");
    } finally {
      setSavingId(null);
    }
  };

  if (!loaded) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-xl bg-zinc-100 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start gap-2 mb-3">
        <SparklesIcon className="h-4 w-4 text-[#5a8a1f] mt-0.5 shrink-0" />
        <p className="text-xs text-zinc-600 leading-relaxed">
          Pick how the AI should sound when it drafts messages for you.
          Affects every AI Message you compose. You can change this any time.
        </p>
      </div>

      <ul className="space-y-2" role="radiogroup" aria-label="AI message tone">
        {MESSAGE_TONES.map((tone) => {
          const isSelected = tone.id === selectedId;
          const isSaving = savingId === tone.id;
          return (
            <li key={tone.id}>
              <button
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => pick(tone.id)}
                disabled={isSaving}
                className={clsx(
                  'w-full text-left rounded-xl border p-3.5 transition-colors flex items-start gap-3',
                  isSelected
                    ? 'border-[#b5f03d] bg-[#b5f03d]/10'
                    : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5',
                  isSaving && 'opacity-60 cursor-wait',
                )}
              >
                {/* Selected indicator — lime checkmark or empty circle */}
                <div className="mt-0.5 shrink-0">
                  {isSelected ? (
                    <CheckCircleIcon className="h-5 w-5 text-[#5a8a1f]" />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-zinc-300" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm font-bold text-[#0d1f14]"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.2px' }}
                  >
                    {tone.label}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5 leading-snug">
                    {tone.description}
                  </p>
                  <p className="text-[11px] text-zinc-400 mt-1.5 italic leading-snug">
                    {tone.example}
                  </p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
